import type { ReactElement } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { routePaths } from '../app/route-paths.js';
import styles from '../components/layout/AuthLayout.module.css';
import { Button } from '../components/ui/Button.js';
import { Spinner } from '../components/ui/Spinner.js';
import { authClient } from '../lib/auth-client.js';
import { safeReturnTo } from '../lib/return-to.js';

/**
 * Landing page for the verification link. Better Auth verifies the token on the API, signs the
 * user in and redirects here; on failure it appends `?error=...` instead.
 */
export function VerifyEmailPage(): ReactElement {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const session = authClient.useSession();
    const linkError = params.get('error');
    const next = safeReturnTo(params.get('next'));

    if (linkError !== null) {
        return (
            <>
                <h1>Link not valid</h1>
                <p>
                    This verification link has expired or was already used. Sign in to request a new
                    one.
                </p>
                <p className={styles.links}>
                    <Link to={routePaths.signIn}>Sign in</Link>
                </p>
            </>
        );
    }

    if (session.isPending) {
        return <Spinner label="Checking your session" />;
    }

    return (
        <>
            <h1>Email verified</h1>
            <p>
                {session.data
                    ? 'Your account is ready. You are signed in on this device.'
                    : 'Your email is confirmed. Sign in to get started.'}
            </p>
            <Button
                type="button"
                onClick={() => {
                    void navigate(session.data ? next : routePaths.signIn);
                }}
            >
                {session.data ? 'Continue' : 'Sign in'}
            </Button>
        </>
    );
}
